import { Alert, AlertTitle, Button, DialogContent, Divider, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import type { ApiResponse, BooleanOperationResult } from "@entrilia/shared/api/types.ts";
import DialogButtons from "@entrilia/shared/components/DialogButtons.tsx";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext.ts";
import useRequest from "@entrilia/shared/hooks/useRequest.ts";
import type {
  GroupForSharing,
  ReportInfo,
  UpdateReportsAuthorizationRequest,
  UserForSharing,
} from "../../../../../../../../../api/types/svTypes.ts";
import CompanySharingRow from "../CompanySharingRow.tsx";
import { EMPTY_GROUP_ID } from "../contexts/SharingReportContextProvider.tsx";
import FollowGroupSharingSwitch from "../FollowGroupSharingSwitch.tsx";
import type { CurrentUser, ReportCompany } from "../ShareReport.types.ts";
import AuthorizedGroupsList from "../userGroups/AuthorizedGroupsList.tsx";
import { buildAuthorizedGroupItems, buildAuthorizedUserItems } from "../userGroups/authorizedItems.ts";
import AuthorizedUsersList from "../userGroups/AuthorizedUsersList.tsx";
import UserSelection from "../UserSelection.tsx";
import LoseAccessConfirmation from "./LoseAccessConfirmation.tsx";
import {
  addGroupsAction,
  addUsersAction,
  changeGroupAccessAction,
  changeUserAccessAction,
  getAuthorizationToApply,
  getInitialShareReportsBulkState,
  isSharingChanged,
  removeGroupAction,
  removeUserAction,
  setCompanyAccessAction,
  setInheritFromParentAction,
  willLoseOwnAccess,
} from "./shareReportsBulkState.ts";

interface Props {
  reports: ReportInfo[];
  currentUser: CurrentUser;
  company: ReportCompany;
  users: UserForSharing[];
  groups: GroupForSharing[];
  doUpdateSharing: (request: UpdateReportsAuthorizationRequest) => Promise<ApiResponse<BooleanOperationResult>>;
  onShared: () => void;
  onClose: () => void;
}

const ShareReportsBulkContent = ({
  reports,
  currentUser,
  company,
  users,
  groups,
  doUpdateSharing,
  onShared,
  onClose,
}: Props) => {
  const { hasAnyPermissionForClient } = useClientPermissionsContext();

  const canInherit = reports.every((r) => r.groupId && r.groupId !== EMPTY_GROUP_ID);
  const defaultInheritFromParent = canInherit && reports.every((r) => r.inheritFromParent);

  const [state, setState] = useState(() => getInitialShareReportsBulkState(defaultInheritFromParent));
  const [showLoseAccessConfirmation, setShowLoseAccessConfirmation] = useState(false);

  const updateSharing = useCallback(
    () =>
      doUpdateSharing({
        clientCode: company.clientCode,
        reportIds: reports.map((r) => r.reportId),
        authorization: getAuthorizationToApply(state),
        inheritFromParent: state.inheritFromParent,
      }),
    [doUpdateSharing, company.clientCode, reports, state]
  );

  const { request, error, isLoading: isSaving } = useRequest(updateSharing);

  const authorizedGroupItems = useMemo(
    () => buildAuthorizedGroupItems(state.authorizedGroups, groups),
    [state.authorizedGroups, groups]
  );
  const authorizedUserItems = useMemo(
    () => buildAuthorizedUserItems(state.authorizedUsers, users, currentUser.id, ReportAccessType.FullAccess),
    [state.authorizedUsers, users, currentUser.id]
  );

  const handleUsersSelected = useCallback((selected: UserForSharing[], access: ReportAccessType) => {
    setState(addUsersAction(selected, access));
  }, []);

  const handleGroupsSelected = useCallback((selected: GroupForSharing[], access: ReportAccessType) => {
    setState(addGroupsAction(selected, access));
  }, []);

  const handleUserAccessChanged = useCallback((userId: string, access: ReportAccessType) => {
    setState(changeUserAccessAction(userId, access));
  }, []);

  const handleGroupAccessChanged = useCallback((groupId: string, access: ReportAccessType) => {
    setState(changeGroupAccessAction(groupId, access));
  }, []);

  const handleRemoveUser = useCallback((userId: string) => setState(removeUserAction(userId)), []);
  const handleRemoveGroup = useCallback((groupId: string) => setState(removeGroupAction(groupId)), []);
  const handleCompanyAccessSelected = useCallback((access: ReportAccessType) => {
    setState(setCompanyAccessAction(access));
  }, []);
  const handleInheritChanged = useCallback((checked: boolean) => setState(setInheritFromParentAction(checked)), []);

  const applySharing = useCallback(async () => {
    const [, requestError] = await request();
    if (!requestError) {
      onShared();
      onClose();
    }
  }, [request, onShared, onClose]);

  const isChanged = isSharingChanged(state, defaultInheritFromParent);
  const hasMembersWithAccess = authorizedGroupItems.length > 0 || authorizedUserItems.length > 0;
  const isReportAdmin = hasAnyPermissionForClient(company.clientCode, ["ManageAllReports"]);
  const notOwnedReportsCount = reports.filter((r) => !r.authorization.own).length;

  const handleApplyClick = async () => {
    if (!isReportAdmin && willLoseOwnAccess(state, currentUser.id, notOwnedReportsCount === 0)) {
      setShowLoseAccessConfirmation(true);
      return;
    }
    await applySharing();
  };

  const handleLoseAccessConfirmed = async () => {
    setShowLoseAccessConfirmation(false);
    await applySharing();
  };

  return (
    <>
      <DialogContent sx={{ pt: 0.5 }}>
        <Stack spacing={2} useFlexGap sx={{ width: "100%" }}>
          {error && <Alert severity="error">An error occurred while updating the sharing.</Alert>}
          {!state.inheritFromParent && (
            <Alert severity="info">
              The selected sharing replaces the current sharing of all {reports.length} reports. Each report keeps its
              own owner.
            </Alert>
          )}
          {canInherit && (
            <FollowGroupSharingSwitch
              checked={state.inheritFromParent}
              disabled={isSaving}
              onChange={handleInheritChanged}
            />
          )}
          {state.inheritFromParent ? (
            <Alert severity="info">
              <AlertTitle>Access follows the group</AlertTitle>
              Manage who can open these reports from their group&apos;s sharing settings.
              <br />
              The owner always keeps access.
            </Alert>
          ) : (
            <>
              <CompanySharingRow
                company={company}
                access={state.companyAccess}
                disabled={isSaving}
                onSelect={handleCompanyAccessSelected}
              />
              <UserSelection
                disabled={isSaving}
                availableUsers={users}
                availableGroups={groups}
                authorizedUsers={state.authorizedUsers}
                authorizedGroups={state.authorizedGroups}
                currentUserId={currentUser.id}
                onUsersSelected={handleUsersSelected}
                onGroupsSelected={handleGroupsSelected}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      {!state.inheritFromParent && hasMembersWithAccess && (
        <>
          <Divider />
          <DialogContent sx={{ pt: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Members with access
            </Typography>
            <Stack sx={{ pt: 1, gap: 2 }}>
              <AuthorizedGroupsList
                items={authorizedGroupItems}
                disabled={isSaving}
                onAccessChanged={handleGroupAccessChanged}
                onRemoveGroup={handleRemoveGroup}
              />
              <AuthorizedUsersList
                items={authorizedUserItems}
                disabled={isSaving}
                onAccessChanged={handleUserAccessChanged}
                onRemoveUser={handleRemoveUser}
              />
            </Stack>
          </DialogContent>
        </>
      )}
      <Divider />
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" color="primary" disabled={!isChanged} loading={isSaving} onClick={handleApplyClick}>
          Apply
        </Button>
      </DialogButtons>
      {showLoseAccessConfirmation && (
        <LoseAccessConfirmation
          reportsCount={notOwnedReportsCount}
          onCancel={() => setShowLoseAccessConfirmation(false)}
          onConfirm={handleLoseAccessConfirmed}
        />
      )}
    </>
  );
};

export default ShareReportsBulkContent;
